import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import SelectSearchField from 'components/molecules/select-search-field'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { Button, Table } from 'reactstrap'

const propTypes = {
  name     : PropTypes.string,
  label    : PropTypes.string,
  required : PropTypes.bool,
  sellerId : PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  selectedItemIds : PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ])
  ),
  showSellerInventoryList : PropTypes.bool,
  onAddInventory          : PropTypes.func
}

const defaultProps = {
  name                    : '',
  label                   : '',
  required                : true,
  sellerId                : '',
  disabled                : false,
  formText                : '',
  selectedItemIds         : [],
  showSellerInventoryList : false,
  onAddInventory          : () => {}
}

const getInventoryMaxQuantity = (inventory) => {
  if (!inventory) return 0

  const storedQuantity = Number(inventory.stored_quantity ?? inventory.stored_quantity_value ?? inventory.quantity ?? 0)

  return storedQuantity > 0 ? storedQuantity : 0
}

const EXCLUDED_CONSIGNOR_RETURN_GROUP_CODES = new Set([ 'BKA', 'BKV' ])

const getInventoryProductPhotoUrl = (inventory) => (
  inventory?.product_photo_url ||
  inventory?.product_image_url ||
  inventory?.main_image_url ||
  inventory?.photo_url ||
  inventory?.product?.photo_url ||
  inventory?.product?.main_image_url ||
  (Array.isArray(inventory?.product_photo_urls) ? inventory.product_photo_urls.find(Boolean) : null) ||
  (Array.isArray(inventory?.product_image_urls) ? inventory.product_image_urls.find(Boolean) : null) ||
  ''
)

const isAvailableInventory = (inventory) => {
  if (!inventory) return false

  return getInventoryMaxQuantity(inventory) > 0
}

const isConsignorReturnAllowedGroup = (inventory) => {
  const groupCode = (inventory?.product_group_code || inventory?.group_code || inventory?.product?.group_code || inventory?.product?.group?.code || '').toString().toUpperCase()

  return !EXCLUDED_CONSIGNOR_RETURN_GROUP_CODES.has(groupCode)
}

const getInventorySellerId = (inventory) => (
  inventory?.seller_id ||
  inventory?.consignor_id ||
  inventory?.seller?.id ||
  inventory?.consignor?.id ||
  inventory?.user_id ||
  inventory?.user?.id ||
  ''
)

const getInventorySellerName = (inventory) => (
  inventory?.seller_name ||
  inventory?.consignor_seller_name ||
  inventory?.consignor_name ||
  inventory?.seller?.name ||
  inventory?.consignor?.name ||
  '-'
)

const isInventoryOwnedBySeller = (inventory, sellerId) => {
  if (!sellerId) return false
  if (inventory?.belongs_to_selected_seller === true) return true
  if (inventory?.belongs_to_selected_seller === false) return false

  return String(getInventorySellerId(inventory)) === String(sellerId)
}

const InventorySelectSearchField = ({
  name,
  label,
  required,
  sellerId,
  disabled,
  formText,
  selectedItemIds,
  showSellerInventoryList,
  onAddInventory,
  ...props
}) => {
  const [ searchInventories, setSearchInventories ] = useState([])
  const [ isSearchLoading, setSearchLoading ]       = useState(false)
  const [ showAllInventory, setShowAllInventory ]   = useState(false)
  const searchTimeoutRef                            = useRef(null)

  const getInventories = (keyword = '') => {

    if (!sellerId) {
      setSearchInventories([])
      setSearchLoading(false)
      return
    }

    setSearchLoading(true)

    InventoriesApiV2.search(keyword, {
      eligible_for_seller_return : true,
      seller_id                  : sellerId,
      show_all_inventory         : showAllInventory,
      per_page                   : 50
    })
      .then((response) => {
        const availableInventories = (response.data.inventories || []).filter((inventory) => {
          if (!isAvailableInventory(inventory)) return false
          if (!isConsignorReturnAllowedGroup(inventory)) return false
          if (showAllInventory) return true

          return isInventoryOwnedBySeller(inventory, sellerId)
        })

        setSearchInventories(availableInventories)
        setSearchLoading(false)
      })
      .catch((error) => {
        setSearchLoading(false)
        alert(error?.response?.data?.messages?.base || error?.response?.data?.message || error.message)
      })
  }

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)

    getInventories('')
  }, [ sellerId, showAllInventory ]) // eslint-disable-line react-hooks/exhaustive-deps

  const getOptionLabel = (option) => {
    const ownedBySelectedSeller = isInventoryOwnedBySeller(option, sellerId)

    return (
      <>
        <span className='mr-3'>{ option.inventory_sku }</span>
        <span className='text-muted'>{ option.product_name }</span>
        {
          showAllInventory && !ownedBySelectedSeller &&
          <span className='badge badge-warning ml-2'>{ 'Different consignor' }</span>
        }
      </>
    )
  }

  const getSelectedOptionLabel = (option) => {
    const ownedBySelectedSeller = isInventoryOwnedBySeller(option, sellerId)

    return (
      <>
        <span className='mr-3'>{ option.inventory_sku }</span>
        <span className='text-muted'>{ option.product_name }</span>
        {
          showAllInventory && !ownedBySelectedSeller &&
          <span className='badge badge-warning ml-2'>{ 'Different consignor' }</span>
        }
      </>
    )
  }

  const isInventoryAlreadySelected = (inventory) => (
    selectedItemIds.map((id) => String(id)).includes(String(inventory.id))
  )

  const handleAddInventory = (inventory) => {
    if (!inventory || isInventoryAlreadySelected(inventory)) return

    onAddInventory(inventory)
  }

  const handleSelectSearchOnChange = (event) => {
    const searchText = event.target.value

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)

    searchTimeoutRef.current = setTimeout(() => {
      getInventories(searchText)
    }, 400)
  }

  const handleToggleShowAllInventory = () => {
    setShowAllInventory((currentValue) => !currentValue)
  }

  const renderSellerInventoryList = () => {
    if (!showSellerInventoryList || !sellerId) return null

    return (
      <div className='mb-4'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <div>
            <h5 className='mb-0'>{ showAllInventory ? 'All Inventory dengan IQ > 0' : 'Inventory milik consignor' }</h5>
            <small className='text-muted'>
              {
                isSearchLoading
                  ? 'Loading inventory...'
                  : `${searchInventories.length} product non-BKA/BKV dengan IQ > 0 ditampilkan`
              }
            </small>
          </div>
          <Button
            color={ showAllInventory ? 'secondary' : 'warning' }
            size='sm'
            type='button'
            onClick={ handleToggleShowAllInventory }
          >
            { showAllInventory ? 'Show Consignor Inventory Only' : 'Show ALL Inventory' }
          </Button>
        </div>

        <Table bordered responsive size='sm' className='mb-0'>
          <thead>
            <tr>
              <th>{ 'Foto Product' }</th>
              <th>{ 'SKU Product' }</th>
              <th>{ 'Product Name' }</th>
              <th>{ 'Consignor Owner' }</th>
              <th>{ 'SQ' }</th>
              <th>{ 'IQ' }</th>
              <th>{ 'Action' }</th>
            </tr>
          </thead>
          <tbody>
            {
              isSearchLoading
                ? (
                  <tr>
                    <td colSpan='7' className='text-center text-muted'>
                      { showAllInventory ? 'Loading all inventory...' : 'Loading inventory consignor...' }
                    </td>
                  </tr>
                )
                : searchInventories.length === 0
                  ? (
                    <tr>
                      <td colSpan='7' className='text-center text-muted'>
                        { showAllInventory ? 'Tidak ada inventory non-BKA/BKV dengan IQ > 0.' : 'Tidak ada product non-BKA/BKV dengan IQ > 0 untuk consignor ini.' }
                      </td>
                    </tr>
                  )
                  : searchInventories.map((inventory) => {
                    const isAlreadySelected     = isInventoryAlreadySelected(inventory)
                    const maxQuantity           = getInventoryMaxQuantity(inventory)
                    const ownedBySelectedSeller = isInventoryOwnedBySeller(inventory, sellerId)

                    return (
                      <tr key={ inventory.id } className={ !ownedBySelectedSeller ? 'table-warning' : '' }>
                        <td>
                          {
                            getInventoryProductPhotoUrl(inventory)
                              ? (
                                <img
                                  src={ getInventoryProductPhotoUrl(inventory) }
                                  alt={ inventory.product_name || inventory.inventory_sku }
                                  style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '6px' }}
                                />
                              )
                              : '-'
                          }
                        </td>
                        <td>{ inventory.product_sku || inventory.inventory_sku }</td>
                        <td>{ inventory.product_name }</td>
                        <td>
                          <div>{ getInventorySellerName(inventory) }</div>
                          {
                            !ownedBySelectedSeller &&
                            <small className='text-danger font-weight-bold'>{ 'Bukan milik consignor terpilih' }</small>
                          }
                        </td>
                        <td>{ inventory.sellable_quantity ?? inventory.sellable_quantity_value ?? 0 }</td>
                        <td>{ inventory.stored_quantity ?? inventory.stored_quantity_value ?? inventory.quantity ?? 0 }</td>
                        <td>
                          <Button
                            color={ isAlreadySelected ? 'secondary' : ownedBySelectedSeller ? 'primary' : 'warning' }
                            size='sm'
                            type='button'
                            onClick={ () => handleAddInventory(inventory) }
                            disabled={ isAlreadySelected || maxQuantity <= 0 }
                          >
                            { isAlreadySelected ? 'Added' : 'Add' }
                          </Button>
                        </td>
                      </tr>
                    )
                  })
            }
          </tbody>
        </Table>
      </div>
    )
  }

  const isDisabled = disabled || !sellerId
  const inventoryFormText = formText || (
    sellerId
      ? showAllInventory
        ? 'Mode Show ALL Inventory aktif. Product yang bukan milik consignor terpilih akan diberi warning dan wajib dikonfirmasi sebelum submit.'
        : 'List inventory consignor dengan IQ > 0 otomatis tampil di bawah. Search bar tetap bisa dipakai untuk mempersempit SKU / nama produk.'
      : 'Pilih consignor terlebih dahulu sebelum mencari inventory.'
  )

  return (
    <>
      <SelectSearchField
        type='text'
        name={ name }
        label={ label }
        placeholder='Search inventory SKU / product SKU / product name'
        dataList={ searchInventories }
        onSearchChange={ handleSelectSearchOnChange }
        getOptionValue={ (option) => option }
        getOptionLabel={ getOptionLabel }
        getSelectedOptionLabel={ getSelectedOptionLabel }
        isLoading={ isSearchLoading }
        required={ required }
        formText={ inventoryFormText }
        { ...props }
        disabled={ isDisabled }
      />
      { renderSellerInventoryList() }
    </>
  )
}

InventorySelectSearchField.propTypes = propTypes
InventorySelectSearchField.defaultProps = defaultProps

export default InventorySelectSearchField
